import {
  BillerEnrollment,
  CreateNewPassword,
  CreateQRCode,
  GeneratedQRCode,
  InviteNewUser,
  Login,
  PaymentFile,
  TransactionList,
  useInject,
  UserDirectory,
} from '@back-office/submerchant';
import {
  GcpBox,
  GcpDialog,
  GcpFallback,
  GcpProgressBar,
  GcpVerticalTabComponent,
  GcpVerticalTabs,
} from '@back-office/ui';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import Container from '@material-ui/core/Container';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import React, { FC, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useHistory } from 'react-router-dom';
import GcpAppBar from './GcpAppbar';
import NotFound from './NotFound';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';

const useStyles = makeStyles(
  (theme) => ({
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: theme.palette.common.ceriseRed,
    },
  }),
  { name: 'App' }
);

const App: FC = observer(() => {
  const { alert, auth, isLoading, isProgress, setStage, stage } = useInject(
    (store) => store
  );
  const [isError, setIsError] = useState<boolean>(false);
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const handleError = (
    error: Error,
    info: { componentStack: string }
  ): void => {
    console.debug(error.message);
    console.trace(error.stack);
    console.info(info.componentStack);
  };

  const handleOnStageChange = (newStage: GcpVerticalTabComponent): void => {
    setStage(newStage);
  };

  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <GcpFallback
          btn="fallback.home"
          handleOnClick={() => {
            resetErrorBoundary();
            if (auth.isAuth) {
              history.push('/');
            } else {
              history.push('/login');
            }
          }}
          message={t(error.message)}
        />
      )}
      resetKeys={[isError]}
      onError={handleError}
      onReset={() => setIsError(false)}
    >
      {isProgress && <GcpProgressBar />}
      <Backdrop className={classes.backdrop} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {alert.open && (
        <GcpDialog
          cancelBtn={alert.cancelBtn}
          handleCancel={alert.handleCancel}
          handleClose={alert.closeDialog}
          handleOk={alert.handleOK}
          isAlert={alert.isAlert}
          message={alert.message}
          okBtn={alert.okBtn}
          open={alert.open}
          title={alert.title}
        />
      )}
      {auth.isAuth && <GcpAppBar />}
      <Container style={{ maxWidth: '1400px' }}>
        <Switch>
          <PrivateRoute exact path="/">
            <GcpBox mt={2.5}>
              <GcpVerticalTabs
                billerEnrollment={<BillerEnrollment />}
                dashboard={t('appbar.dashboard')}
                directory={<UserDirectory />}
                handleOnStageChange={handleOnStageChange}
                invitation={<InviteNewUser />}
                isRecurringMerchant={auth.currentUser?.isRecurringMerchant}
                paymentFile={<PaymentFile />}
                qr={<CreateQRCode />}
                stage={stage as GcpVerticalTabComponent}
                transactions={<TransactionList />}
                userRole={auth.currentUser.role || ''}
                visibleBillerRequest={auth.currentUser?.visibleBillerRequest}
                visiblePaymentRequest={auth.currentUser?.visiblePaymentRequest}
              />
            </GcpBox>
          </PrivateRoute>
          <PublicRoute path="/invitations">
            <CreateNewPassword isForgotPass={false} />
          </PublicRoute>
          <PublicRoute path="/login">
            <Login />
          </PublicRoute>
          <PublicRoute path="/forget_password">
            <CreateNewPassword isForgotPass />
          </PublicRoute>
          <PrivateRoute path="/generated_qr">
            <GeneratedQRCode />
          </PrivateRoute>
          <Route path="*">
            <NotFound />
          </Route>
        </Switch>
      </Container>
    </ErrorBoundary>
  );
});

export default App;
